
import { Component, Emit, Mixins, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import SportWatchNowButton from '@/components/sports/all/SportWatchNowButton.vue';
import CompetitorLogo from '@/components/sports/common/CompetitorLogo.vue';
import BasicButton from '@/ui-components/BasicButton.vue';

import { MultiStepSportEvent, ObjectType, SingleStepSportEvent, SportEventStatus } from '@/@types/graphql-types';

import type { WebLocale } from '@/enums/web-locale';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { BuyboxParam } from '@/helpers/offer-helper';
import { SingleStepContext } from '@/helpers/sports/config';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import ToggleMySportsNotificationMutation from '@/mixins/ToggleMySportsNotificationMutation.vue';

const Constant = namespace('constant');
const Language = namespace('language');

@Component({
	name: 'SingleStepEventLineup',
	components: {
		SportWatchNowButton,
		BasicButton,
		CompetitorLogo,
	},
})
export default class SingleStepEventLineup extends Mixins(ToggleMySportsNotificationMutation) {
	// offers get created with a domainSessionId in their link out url.
	// this is needed since domainSessionId is not available beforehand, but still the link needs to be created with contexts.
	// changing domainSessionId later will also trigger currentOffers anew.
	protected domainSessionId = '' as string | undefined;

	@Prop() displayOffer: boolean;
	@Prop({ default: '' }) kickoffDate: string;
	@Prop({ default: '' }) kickoffTime: string;
	@Prop({ required: true }) context: SingleStepContext;
	@Prop({ required: true }) event: SingleStepSportEvent;

	@Language.Getter country: string;
	@Language.State webLocale: WebLocale;

	get competitors() {
		return this.context.getCompetitors(this.event, this.webLocale);
	}

	get isDoublesEvent() {
		return this.competitors.start.length === 2 && this.competitors.end.length === 2;
	}

	get watchNowContent() {
		return this.isLive ? this.$t('SPORTS_WEBAPP_WATCH_LIVE') : this.$t('SPORTS_WEBAPP_WATCH_HIGHLIGHTS');
	}

	get gameStatusContent() {
		return this.isEnded ? this.$t('SPORTS_WEBAPP_FULL_TIME') : this.$t('SPORTS_WEBAPP_LIVE');
	}

	get notStarted() {
		return this.event.content.status === SportEventStatus.NotStarted;
	}

	get isEnded() {
		return this.event.content.status === SportEventStatus.Closed;
	}

	get isLive() {
		return this.event.content.status === SportEventStatus.Live;
	}

	get showWatchNow() {
		return !this.notStarted && this.displayOffer;
	}

	get showNotifyMe() {
		return this.notStarted && this.displayOffer;
	}

	get offerClickoutUrl() {
		return new ClickoutUrl(this.event.watchNowOffer)
			.set('uct_country', this.country)
			.set('uct_buybox', BuyboxParam.Sport)
			.set('sid', this.domainSessionId)
			.set('uct_sport', this.event.sport)
			.toString();
	}

	get showScore() {
		return this.competitors.start[0].score !== null && this.competitors.end[0].score !== null && this.isEnded;
	}

	get isNotificationActive() {
		return !!this.event.mysportlistEntry?.createdAt;
	}

	@Emit('clickOut')
	handleClickOut() {
		return { offer: this.event.watchNowOffer, index: undefined };
	}

	async notifyMe(event: SingleStepSportEvent | MultiStepSportEvent) {
		await this.toggleMySportsNotification({
			eventId: this.event.id,
			isNotificationActive: !!this.event.mysportlistEntry?.createdAt,
		});

		const sportContext = SnowplowSportContext.fromSportEventV2(event.sport, ObjectType.SportEvent, event);
		TrackingHelper.trackEvent('userinteraction', { action: 'notify_me_clicked' }, [sportContext]);
	}
}
